/**
 * Which card sits where on the dashboard, and how that survives a reader rearranging it.
 *
 * The order is a list of ids rather than positions, so it says only "this before that" and stays
 * true whatever the grid does with it. `DashboardCardGrid` fits as many columns across as the pane
 * allows and is deliberately tuned to no particular card count (#5076); reordering has to leave
 * that alone, and a list of ids does.
 */

export const dashboardCardIds = [
  "queue",
  "conversations",
  "work",
  "upcoming-events",
  "calendar-month",
  "activity",
] as const;

export type DashboardCardId = (typeof dashboardCardIds)[number];

/** Where the reader's own order is kept. Per browser, which is the whole of its promise (#5141). */
export const dashboardCardOrderStorageKey = "attorneyhub.dashboard.card-order";

export function isDashboardCardId(value: unknown): value is DashboardCardId {
  return typeof value === "string" && (dashboardCardIds as readonly string[]).includes(value);
}

/**
 * A saved order, made safe to render from.
 *
 * Saved ids come first in the order they were saved, and any card the saved order has never heard
 * of is appended in its default position. That is what lets a card be added to the dashboard later
 * without stranding every reader who has already dragged something: they keep their arrangement and
 * the new card turns up at the end rather than disappearing. Ids that no longer exist are dropped
 * for the same reason in reverse.
 *
 * It takes `unknown` because what it is handed came out of storage a previous version of this code
 * wrote, and nothing guarantees it is still a list of strings.
 */
export function applyDashboardCardOrder(saved: unknown): DashboardCardId[] {
  const known = (Array.isArray(saved) ? saved : []).filter(isDashboardCardId);
  const seen = new Set<DashboardCardId>();
  const order: DashboardCardId[] = [];
  for (const id of known) {
    if (seen.has(id)) continue;
    seen.add(id);
    order.push(id);
  }
  for (const id of dashboardCardIds) {
    if (!seen.has(id)) order.push(id);
  }
  return order;
}

export function defaultDashboardCardOrder(): DashboardCardId[] {
  return applyDashboardCardOrder([]);
}

/** The order with `cardId` moved to `toIndex`, clamped to the ends rather than falling off them. */
export function moveDashboardCard(
  order: readonly DashboardCardId[],
  cardId: DashboardCardId,
  toIndex: number,
): DashboardCardId[] {
  const from = order.indexOf(cardId);
  if (from < 0) return [...order];

  const to = Math.min(Math.max(toIndex, 0), order.length - 1);
  if (to === from) return [...order];

  const moved = [...order];
  moved.splice(from, 1);
  moved.splice(to, 0, cardId);
  return moved;
}

/** The order after a card has been dropped onto another one: the dragged card takes its place. */
export function reorderDashboardCards(
  order: readonly DashboardCardId[],
  activeId: unknown,
  overId: unknown,
): DashboardCardId[] {
  if (!isDashboardCardId(activeId) || !isDashboardCardId(overId)) return [...order];
  if (activeId === overId) return [...order];
  return moveDashboardCard(order, activeId, order.indexOf(overId));
}

/**
 * Reading and writing the reader's order, on a storage that is allowed to refuse.
 *
 * `localStorage` throws rather than returning nothing when a browser is set to block site data, and
 * a dashboard that will not render because of a layout preference is a worse failure than one drawn
 * in the default order. Both directions swallow it and carry on.
 */
export function readDashboardCardOrder(storage: Pick<Storage, "getItem"> | undefined): DashboardCardId[] {
  try {
    const saved = storage?.getItem(dashboardCardOrderStorageKey);
    if (!saved) return defaultDashboardCardOrder();
    return applyDashboardCardOrder(JSON.parse(saved));
  } catch {
    return defaultDashboardCardOrder();
  }
}

export function writeDashboardCardOrder(
  storage: Pick<Storage, "setItem"> | undefined,
  order: readonly DashboardCardId[],
): void {
  try {
    storage?.setItem(dashboardCardOrderStorageKey, JSON.stringify(order));
  } catch {
    // A reader who cannot save their arrangement still gets to have it for this visit.
  }
}

/**
 * Which cards the reader has put away.
 *
 * Kept apart from the order rather than folded into it, so that unhiding a card returns it to
 * where the reader had it rather than to the end of the list — and so an order saved before any of
 * this existed still reads correctly.
 */
export const dashboardHiddenCardsStorageKey = "attorneyhub.dashboard.hidden-cards";

export function applyHiddenCards(saved: unknown): DashboardCardId[] {
  const known = (Array.isArray(saved) ? saved : []).filter(isDashboardCardId);
  return [...new Set(known)];
}

export function toggleHiddenCard(
  hidden: readonly DashboardCardId[],
  cardId: DashboardCardId,
): DashboardCardId[] {
  return hidden.includes(cardId)
    ? hidden.filter((id) => id !== cardId)
    : [...hidden, cardId];
}

export function readHiddenCards(storage: Pick<Storage, "getItem"> | undefined): DashboardCardId[] {
  try {
    const saved = storage?.getItem(dashboardHiddenCardsStorageKey);
    if (!saved) return [];
    return applyHiddenCards(JSON.parse(saved));
  } catch {
    return [];
  }
}

export function writeHiddenCards(
  storage: Pick<Storage, "setItem"> | undefined,
  hidden: readonly DashboardCardId[],
): void {
  try {
    storage?.setItem(dashboardHiddenCardsStorageKey, JSON.stringify(hidden));
  } catch {
    // A reader who cannot save their choice still gets to make it for this visit.
  }
}

/**
 * Whether the reader has changed anything, which is what "Reset" has to undo.
 *
 * Asked rather than assumed so the control can say plainly that there is nothing to reset, instead
 * of offering an action that does nothing.
 */
export function isDashboardCustomised(
  order: readonly DashboardCardId[],
  hidden: readonly DashboardCardId[],
): boolean {
  if (hidden.length > 0) return true;
  const fallback = defaultDashboardCardOrder();
  return order.length !== fallback.length || order.some((id, index) => id !== fallback[index]);
}

/**
 * Puts the dashboard back the way it ships, from somewhere other than the dashboard — Settings.
 *
 * The dashboard's own "Reset to the default layout" sets its state as well; this only forgets
 * what was saved, which is enough because the dashboard reads storage each time it mounts and
 * absence means the default.
 */
export function clearDashboardLayout(storage: Pick<Storage, "removeItem"> | undefined): void {
  try {
    storage?.removeItem(dashboardCardOrderStorageKey);
    storage?.removeItem(dashboardHiddenCardsStorageKey);
  } catch {
    // Storage the browser refuses held nothing to clear.
  }
}
